import { useCallback, useMemo, useState } from "react";

import useWindowSize from "@/hooks/use-window-size/use-window-size";
import { BrandKey } from "@/types/common-types";
import { RenewalRightPanelPageDataType } from "@/types/renewals";

export function useRightPanelBrandStyles(
  pageData: RenewalRightPanelPageDataType,
  activeSection: string,
  brand: BrandKey
) {
  const windowSize = useWindowSize() ?? 0;
  const [bgVisible, setBgVisible] = useState<boolean>(false);
  const [bgAvailable, setBgAvailable] = useState<boolean | null>(null);

  const brandStyles = useMemo(() => {
    const trustPilot =
      "[&>div:last-child>span]:text-text-body [&>div:first-child>svg>g>path:first-child]:fill-text-body";
    const titleColor = "text-surface-neutral-option-1";
    const descriptionColor = "text-dark-text-body";
    const colorAVN = "text-interactive-secondary-default";

    const accordionBasedStylesAVN: Record<
      string,
      {
        trustPilot: string;
        titleColor: string;
        descriptionColor: string;
      }
    > = {
      "policy-details": {
        trustPilot,
        titleColor: colorAVN,
        descriptionColor: colorAVN,
      },
      travellers: {
        trustPilot,
        titleColor: colorAVN,
        descriptionColor: colorAVN,
      },
      "medical-conditions": {
        trustPilot: "",
        titleColor,
        descriptionColor,
      },
      "policy-extras": {
        trustPilot:
          "[&>div:last-child>span]:text-surface-neutral-option-1 [&>div:first-child>svg>g>path:first-child]:text-surface-neutral-option-1",
        titleColor: "text-[#F0F0F0]",
        descriptionColor: "text-[#F2EDE8]",
      },
      "contact-details": {
        trustPilot,
        titleColor: colorAVN,
        descriptionColor: colorAVN,
      },
    };

    const colorsAVN = accordionBasedStylesAVN[activeSection];

    const style = {
      staysure: {
        trustPilot: "",
        titleColor,
        descriptionColor,
      },
      avanti: {
        trustPilot: colorsAVN.trustPilot,
        titleColor: colorsAVN.titleColor,
        descriptionColor: colorsAVN.descriptionColor,
      },
      expat: {
        trustPilot: "",
        titleColor: "",
        descriptionColor: "",
      },
    };
    return style[brand];
  }, [activeSection, brand]);

  const selectedSection = useCallback(() => {
    return (
      pageData.menuItems.find((item) => item.uniqueId === activeSection) || null
    );
  }, [activeSection, pageData.menuItems]);

  const getBgStyles = useMemo(() => {
    const section = selectedSection();
    let imageUrl = null;

    if (section) {
      if (windowSize && windowSize < 600) {
        imageUrl = section.mobileImageUrl;
      } else if (windowSize && windowSize < 904) {
        imageUrl = section.tabletImageUrl;
      } else if (windowSize) {
        imageUrl = section.desktopImageUrl;
      }
    }

    const bgImageSrc = imageUrl
      ? `${process.env.NEXT_PUBLIC_MGNL_HOST}/dam/${imageUrl}`
      : null;

    if (bgImageSrc) {
      setBgAvailable(true);
      const gradient =
        brand === "avanti"
          ? ""
          : "linear-gradient(0deg, rgba(0, 0, 0, 0.30) 0%, rgba(0, 0, 0, 0.30) 100%)";
      return {
        url: bgImageSrc,
        style: {
          backgroundPosition: "center",
          background: bgVisible ? `${gradient}` : "transparent",
        },
      };
    }
    setBgAvailable(false);
    setBgVisible(false);
    return {
      url: "",
      style: {
        backgroundColor: `var(--bg-dark-surface-accent-1-primary, ${
          brand === "avanti" ? "#005A5E" : "#522367"
        })`,
      },
    };
  }, [selectedSection, windowSize, bgVisible, brand]);

  const styles = {
    getBgStyles,
    brandStyles,
  };

  const states = {
    bgAvailable,
    bgVisible,
  };

  const handlers = {
    setBgVisible,
    setBgAvailable,
    selectedSection,
  };

  return { styles, states, handlers };
}
